/**
 * Survival containers sharing the player-inventory block: the inventory (2x2
 * craft, armor, offhand, player preview), the crafting table (3x3), the furnace
 * and the chest. All coordinates are vanilla's (see docs/survival-architecture.md).
 */

import { type Stack, isEmpty } from '../../game/types';
import { CLICK, chestMenu, craftingMenu, furnaceMenu, inventoryMenu, type CraftMenu, type Menu } from '../../game/menu';
import type { FurnaceState } from '../../game/furnace';
import { GuiStage, sheet } from '../kit';
import { ContainerScreen } from './container';
import { attachRecipeBook, attachRecipeList } from './recipeBook';
import { smeltingRows } from '../../game/recipeBook';
import { nameOf } from '../../items';
import type { ScreenDeps } from './screenHost';

const ARMOR_ICONS = ['helmet', 'chestplate', 'leggings', 'boots'].map((n) => `gui/sprites/container/slot/${n}.png`);

/** Crafting grid + result plumbing shared by the 2x2 and 3x3 screens (rules: game/menu.ts). */
abstract class CraftingBase extends ContainerScreen {
  protected craft!: CraftMenu;

  /** Position the result (slot 0) and the grid (slots 1..w*h). */
  protected placeGrid(ox: number, oy: number, resultX: number, resultY: number): void {
    const { gw, gh } = this.craft;
    this.at(0, resultX, resultY);
    for (let r = 0; r < gh; r++) for (let c = 0; c < gw; c++) this.at(1 + r * gw + c, ox + c * 18, oy + r * 18);
  }

  /** Recipe book side panel (ui/screens/recipeBook.ts) over this crafting grid. */
  protected attachBook(ox: number, oy: number, rx: number, ry: number, bx: number, by: number): void {
    const { gw, gh } = this.craft;
    const cells: Array<{ x: number; y: number }> = [];
    for (let r = 0; r < gh; r++) for (let c = 0; c < gw; c++) cells.push({ x: ox + c * 18, y: oy + r * 18 });
    attachRecipeBook(this, {
      gw, gh, cells, result: { x: rx, y: ry }, grid: this.craft.grid,
      onGrid: () => {
        this.craft.recompute!();
        // server mode: the book moved items without clicks, so send the grid it built
        const each = this.craft.grid.reduce((n, s) => Math.max(n, s.count), 0);
        if (each > 0) this.report(null, each, CLICK.FILL, { grid: this.craft.grid.map((s) => (isEmpty(s) ? 0 : s.id)) });
      },
      button: { x: bx, y: by },
    });
  }
}

export class InventoryScreen extends CraftingBase {
  private readonly preview: HTMLCanvasElement;

  constructor(deps: ScreenDeps, stage: GuiStage) {
    super(deps, stage);
    this.root.appendChild(sheet('gui/container/inventory.png', 0, 0, 176, 166, 0, 0));
    // 0 result, 1..4 craft grid, 5..8 armor, 9..35 main, 36..44 hotbar, 45 offhand
    this.craft = this.use(inventoryMenu(this.env()));
    this.placeGrid(98, 18, 154, 28);
    for (let i = 0; i < 4; i++) this.at(5 + i, 8, 8 + i * 18, { emptyIcon: ARMOR_ICONS[i] });
    this.placePlayer(9, 84, 142);
    this.at(45, 77, 62, { emptyIcon: 'gui/sprites/container/slot/shield.png' });
    this.addLabel('Crafting', 97, 8);
    this.attachBook(98, 18, 154, 28, 104, 61);
    this.preview = document.createElement('canvas');
    this.preview.style.cssText = 'position:absolute;left:26px;top:8px;width:49px;height:70px;pointer-events:none;';
    this.root.appendChild(this.preview);
    this.mount();
  }

  override update(): void {
    // vanilla InventoryScreen look vector: pointer relative to the preview's centre
    const S = this.stage.scale;
    const w = Math.round(49 * S), h = Math.round(70 * S);
    if (this.preview.width !== w || this.preview.height !== h) { this.preview.width = w; this.preview.height = h; }
    this.deps.renderPlayerPreview(this.preview, this.left + 51 - this.mx, this.top + 75 - 50 - this.my);
  }
}

export class CraftingTableScreen extends CraftingBase {
  constructor(deps: ScreenDeps, stage: GuiStage) {
    super(deps, stage);
    this.root.appendChild(sheet('gui/container/crafting_table.png', 0, 0, 176, 166, 0, 0));
    this.craft = this.use(craftingMenu(this.env())); // 0 result, 1..9 grid, 10..36 main, 37..45 hotbar
    this.placeGrid(30, 17, 124, 35);
    this.placePlayer(10, 84, 142);
    this.addLabel('Crafting', 29, 6);
    this.addLabel('Inventory', 8, 72);
    this.attachBook(30, 17, 124, 35, 5, 34);
    this.mount();
  }
}

export class FurnaceScreen extends ContainerScreen {
  private readonly flame: HTMLDivElement;
  private readonly arrow: HTMLDivElement;

  private readonly furnace: Menu;

  constructor(deps: ScreenDeps, stage: GuiStage, private readonly state: FurnaceState) {
    super(deps, stage);
    this.root.appendChild(sheet('gui/container/furnace.png', 0, 0, 176, 166, 0, 0));
    this.furnace = this.use(furnaceMenu(this.env(), state)); // 0 input, 1 fuel, 2 output, 3..29 main, 30..38 hotbar
    this.at(0, 56, 17); this.at(1, 56, 53); this.at(2, 116, 35);
    this.placePlayer(3, 84, 142);
    this.addLabel('Furnace', 88, 6, 'center');
    this.addLabel('Inventory', 8, 72);
    attachRecipeList(this, { x: 5, y: 17 }, 'Smelting', smeltingRows(),
      (r) => [`${nameOf(r.inputs[0].id)} \u2192 ${nameOf(r.output.id)}${r.output.count > 1 ? ' x' + r.output.count : ''}`]);
    this.flame = document.createElement('div');
    this.flame.style.cssText =
      'position:absolute;left:56px;width:14px;background:url(mc/gui/sprites/container/furnace/lit_progress.png) no-repeat;' +
      'background-size:14px 14px;image-rendering:pixelated;pointer-events:none;';
    this.arrow = document.createElement('div');
    this.arrow.style.cssText =
      'position:absolute;left:79px;top:34px;height:16px;background:url(mc/gui/sprites/container/furnace/burn_progress.png) no-repeat;' +
      'background-size:24px 16px;image-rendering:pixelated;pointer-events:none;';
    this.root.append(this.flame, this.arrow);
    this.mount();
    this.update();
  }

  override update(): void {
    const st = this.state;
    this.furnace.settle!();
    if (st.burnLeft > 0) {
      const total = st.burnTotal > 0 ? st.burnTotal : 200;
      const k = Math.min(14, Math.ceil((st.burnLeft / total) * 13) + 1);
      this.flame.style.display = '';
      this.flame.style.top = 36 + 14 - k + 'px';
      this.flame.style.height = k + 'px';
      this.flame.style.backgroundPosition = `0 ${k - 14}px`;
    } else this.flame.style.display = 'none';
    const aw = st.cookProgress > 0 ? Math.min(24, Math.ceil((st.cookProgress / 200) * 24)) : 0;
    this.arrow.style.width = aw + 'px';
  }
}

export class ChestScreen extends ContainerScreen {
  private readonly rows: number;

  constructor(deps: ScreenDeps, stage: GuiStage, contents: Stack[], title: string) {
    super(deps, stage);
    this.rows = Math.max(1, Math.ceil(contents.length / 9));
    const rows = this.rows;
    this.h = 114 + rows * 18;
    this.root.appendChild(sheet('gui/container/generic_54.png', 0, 0, 176, rows * 18 + 17, 0, 0));
    this.root.appendChild(sheet('gui/container/generic_54.png', 0, 126, 176, 96, 0, rows * 18 + 17));
    this.use(chestMenu(this.env(), contents, rows * 9));
    for (let i = 0; i < rows * 9; i++) this.at(i, 8 + (i % 9) * 18, 18 + Math.floor(i / 9) * 18);
    this.placePlayer(rows * 9, 103 + (rows - 4) * 18, 161 + (rows - 4) * 18);
    this.addLabel(title, 8, 6);
    this.addLabel('Inventory', 8, this.h - 94);
    this.mount();
  }
}
